import React, { useEffect, useState, useReducer, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import FacilityGeneralInfoCard from './FacilityGeneralInfoCard';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { loadCurrencies } from 'actions/actionCreators/dictionariesActions';
import {
  loadPartnersList,
  loadFacilityRevolvingTypes,
  loadFacilityFlexibilityRules,
  loadFacilityCreditLineStatuses,
  loadFacilityPurposesForApplication,
  loadPartnerGroups,
  loadTransferToERPPermissions,
  saveFacilityHeader,
  transferFacilityToERP
} from '../../actionCreators';
import {
  getFacilityRevolvingTypes,
  getFacilityFlexibilityRules,
  getFacilityCreditLineStatuses,
  getFacilityPurposesForApplication,
  getPartnerGroups,
  getTransferToERPPermissions
} from '../../selectors';
import { getFacilityHeader } from '../../../../wizard/selectors';
import debounce from 'lodash.debounce';
import { reducer, initialState } from '../../reducers/info/containerReducer';
import { shape, required, fromModel, between, when, maxLength, stopOnFirstFailure } from '@totalsoft/pure-validations';
import { dateIsGreaterThanGivenDate } from 'utils/pure-validation/primitiveValidators';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { editableStates } from '../../../../../../constants/editableStates';
import { useDirtyInfo } from '@totalsoft/change-tracking-react';
import { minimumChAmount, maximumChAmount, maximumCommentLength } from 'constants/numericLimits';

const contentEqual = (prev, next) =>
  prev.loading === next.loading && prev.loaded === next.loaded && prev.content.length === next.content.length;

const statusEqual = (prev, next) => prev.loading === next.loading && prev.loaded === next.loaded;

const FacilityGeneralInfoContainer = ({ documentId, siteId, folderId, history }) => {
  const dispatch = useDispatch();
  const [localState, localDispatch] = useReducer(reducer, initialState);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [dirtyInfo, setDirtyInfo] = useDirtyInfo();

  const editable = folderId
    ? editableStates.EditableFromSourceDocument
    : !folderId && localState.facilityHeader.offerId
      ? editableStates.NotEditable
      : editableStates.Editable;

  const validatorFacilityHeader =
    editable === editableStates.Editable
      ? fromModel(model =>
        shape({
          documentNumber: maxLength(50),
          partnerGroupId: required |> when(Boolean(!model.partnerId)),
          partnerId: required |> when(Boolean(!model.partnerGroupId)),
          facilityAmount: [required, between(minimumChAmount, maximumChAmount)] |> stopOnFirstFailure,
          currencyId: required,
          revolvingItemId: required,
          validFrom: required,
          validUntil: dateIsGreaterThanGivenDate(model.validFrom) |> when(Boolean(model.validFrom)),
          validityPeriod: between(minimumChAmount, maximumChAmount),
          rating: between(minimumChAmount, maximumChAmount),
          ratingYear: between(1753, 9999) |> when(model.ratingYear !== ''),
          comments: maxLength(maximumCommentLength)
        })
      )
      : fromModel(model =>
        shape({
          documentNumber: maxLength(50),
          facilityAmount: [required, between(minimumChAmount, maximumChAmount)] |> stopOnFirstFailure,
          currencyId: required,
          revolvingItemId: required,
          validFrom: required,
          validUntil: dateIsGreaterThanGivenDate(model.validFrom) |> when(Boolean(model.validFrom)),
          validityPeriod: between(minimumChAmount, maximumChAmount),
          rating: between(minimumChAmount, maximumChAmount),
          ratingYear: between(1753, 9999) |> when(model.ratingYear !== ''),
          comments: maxLength(maximumCommentLength)
        })
      );
  const [validationFacilityHeader, validateFacilityHeader] = useDirtyFieldValidation(validatorFacilityHeader, dirtyInfo);

  const { loaded: facilityHeaderLoaded, content: facilityHeader } = useSelector(
    state => getFacilityHeader(state, documentId),
    statusEqual
  );

  const { content: facilityRevolvingTypes } = useSelector(state => getFacilityRevolvingTypes(state), contentEqual);
  const {
    content: facilityFlexibilityRules,
    loading: facilityFlexibilityRulesLoading,
    loaded: facilityFlexibilityRulesLoaded
  } = useSelector(state => getFacilityFlexibilityRules(state), contentEqual);
  const {
    content: facilityCreditLineStatuses,
    loading: facilityCreditLineStatusesLoading,
    loaded: facilityCreditLineStatusesLoaded
  } = useSelector(state => getFacilityCreditLineStatuses(state), contentEqual);
  const {
    content: facilityPurposesForApplication,
    loading: facilityPurposesForApplicationLoading,
    loaded: facilityPurposesForApplicationLoaded
  } = useSelector(state => getFacilityPurposesForApplication(state), contentEqual);
  const { content: partnerGroups } = useSelector(state => getPartnerGroups(state), contentEqual);
  const { content: transferToERPPermissions, loaded: transferToERPPermissionsLoaded } = useSelector(
    state => getTransferToERPPermissions(state, documentId),
    statusEqual
  );
  const defaultCurrencyId = useSelector(state => state.params.items.defaultCurrencyId, shallowEqual);

  useEffect(() => {
    dispatch(loadFacilityRevolvingTypes());
    dispatch(loadFacilityFlexibilityRules());
    dispatch(loadFacilityCreditLineStatuses());
    dispatch(loadFacilityPurposesForApplication());
    dispatch(loadPartnerGroups());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!documentId) return;
    dispatch(loadTransferToERPPermissions(documentId, siteId));
  }, [documentId, transferToERPPermissionsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    //get translated dictionaries on language change
    if (facilityHeaderLoaded && !facilityFlexibilityRulesLoading && !facilityFlexibilityRulesLoaded)
      dispatch(loadFacilityFlexibilityRules());
    if (facilityHeaderLoaded && !facilityCreditLineStatusesLoading && !facilityCreditLineStatusesLoaded)
      dispatch(loadFacilityCreditLineStatuses());
    if (facilityHeaderLoaded && !facilityPurposesForApplicationLoading && !facilityPurposesForApplicationLoaded)
      dispatch(loadFacilityPurposesForApplication());
  }, [
    facilityFlexibilityRulesLoading,
    facilityFlexibilityRulesLoaded,
    facilityCreditLineStatusesLoading,
    facilityCreditLineStatusesLoaded,
    facilityPurposesForApplicationLoading,
    facilityPurposesForApplicationLoaded,
    facilityHeaderLoaded,
    dispatch
  ]);

  useEffect(() => {
    if (!facilityHeaderLoaded || !documentId) return;
    localDispatch({ type: 'OnInitState', facilityHeader });
    loadPartnersDebounced(facilityHeader.partnerUniqueId);
  }, [facilityHeaderLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    validateFacilityHeader(localState.facilityHeader, dirtyInfo);
  }, [dirtyInfo, localState.facilityHeader]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      (defaultCurrencyId || defaultCurrencyId === 0) &&
      !documentId &&
      !(localState.facilityHeader.currencyId || localState.facilityHeader.currencyId === 0)
    )
      localDispatch({ type: 'OnHeaderValuesChanged', propertyName: 'currencyId', value: defaultCurrencyId });
  }, [defaultCurrencyId, documentId, localState.facilityHeader, localDispatch]);

  const onRedirect = (documentId, siteId) => {
    history.push(`/facilities/${documentId}/${siteId}`);
  };

  const loadPartnersStateRef = useRef();
  loadPartnersStateRef.current = { partnerOptions, partner: localState.facilityHeader.partner };

  const loadPartnersDebounced = useMemo(
    () =>
      debounce(input => {
        const { partnerOptions, partner } = loadPartnersStateRef.current;
        const currentPartner = partnerOptions.find(x => x.partnerId === partner?.partnerId);
        dispatch(loadPartnersList(input)).then(data => {
          const newPartnerOptions = data.map(item => {
            return { partnerId: item.id, partnerUniqueId: item.uniqueId, partnerName: item.partnerName };
          });
          if (currentPartner && !newPartnerOptions.find(x => x.partnerId === currentPartner.partnerId)) {
            newPartnerOptions.push(currentPartner);
          }
          setPartnerOptions(newPartnerOptions);
        });
      }, 500),
    [dispatch]
  );

  const loadCurrenciesInternal = () => {
    return dispatch(loadCurrencies()).then(data => {
      return data.map(item => {
        return { currencyId: item.currencyId, code: item.code };
      });
    });
  };

  const onPropertyChange = useCallback(
    (prop1, prop2) => (value1, value2) => {
      localDispatch({ type: 'OnHeaderValuesChanged', propertyName: prop1, value: value1 });
      setDirtyInfo(`${prop1}`);
      if (prop2) {
        localDispatch({ type: 'OnHeaderValuesChanged', propertyName: prop2, value: value2 });
        setDirtyInfo(`${prop2}`);
      }
      if (prop1 === 'clientName' && value1 === '') {
        localDispatch({ type: 'OnHeaderValuesChanged', propertyName: 'partnerName', value: '' });
        setDirtyInfo('partnerName');
      }
      if (prop2 === 'partnerName' && value2 === '') {
        localDispatch({ type: 'OnHeaderValuesChanged', propertyName: 'clientName', value: '' });
        setDirtyInfo('clientName');
      }
      if (prop1 === 'partnerId') setDirtyInfo('partnerGroupId');
      if (prop1 === 'partnerGroupId') setDirtyInfo('partnerId');
    },
    [setDirtyInfo]
  );

  const onSaveCallBack = (documentId, siteId) => {
    if (!documentId) {
      localDispatch({ type: 'OnHeaderSaveFailed' });
      return;
    }
    localDispatch({ type: 'OnHeaderSaved' });
    if (!facilityHeaderLoaded) onRedirect(documentId, siteId);
  };

  const onSave = () => {
    if (!validateFacilityHeader(localState.facilityHeader)) return;
    localDispatch({ type: 'OnHeaderSaving' });
    const siteId = localState.facilityHeader.siteId;
    const item = Object.assign(localState.facilityHeader);
    dispatch(saveFacilityHeader(item, item.documentId, siteId, onSaveCallBack));
  };

  const openLinkedDocument = (documentId, documentSiteId) => e => {
    e.stopPropagation();
    window.open(`/offers/${documentId}/${documentSiteId}`, '_blank');
  };

  const onTransferFacilityToERPCallBack = eventBody => {
    if (!eventBody || !eventBody.chDocumentId) {
      localDispatch({ type: 'OnTransferFacilityToERPFailed' });
      return;
    }
    localDispatch({ type: 'OnTransferFacilityToERPSucceeded', eventBody });
    if (!facilityHeaderLoaded) onRedirect(eventBody.documentId, eventBody.siteId);
  };

  const onTransferFacilityToERP = () => {
    localDispatch({ type: 'OnTransferFacilityToERPStarted' });
    dispatch(
      transferFacilityToERP(localState.facilityHeader.documentId, localState.facilityHeader.siteId, onTransferFacilityToERPCallBack)
    );
  };

  return (
    <FacilityGeneralInfoCard
      facilityHeader={localState.facilityHeader}
      isSaving={localState.isSaving}
      isTransfering={localState.isTransfering}
      onSave={onSave}
      onTransferFacilityToERP={onTransferFacilityToERP}
      isDirty={localState.isDirty}
      validation={validationFacilityHeader}
      loadCurrencies={loadCurrenciesInternal}
      loadPartners={loadPartnersDebounced}
      partnerOptions={partnerOptions}
      facilityRevolvingTypes={facilityRevolvingTypes}
      facilityFlexibilityRules={facilityFlexibilityRules}
      facilityCreditLineStatuses={facilityCreditLineStatuses}
      facilityPurposesForApplication={facilityPurposesForApplication}
      partnerGroups={partnerGroups}
      transferToERPPermissions={transferToERPPermissions}
      onPropertyChange={onPropertyChange}
      loading={documentId && (!facilityHeaderLoaded || !localState.facilityHeader.documentId)}
      openLinkedDocument={openLinkedDocument}
      editable={editable}
    />
  );
};

FacilityGeneralInfoContainer.propTypes = {
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  folderId: PropTypes.number,
  history: PropTypes.object
};

export default FacilityGeneralInfoContainer;
